import {
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";

import { cn } from "./cn";

export const inputClass =
  "h-12 w-full rounded-xl border border-border-strong bg-surface px-3.5 text-[15px] text-ink placeholder:text-faint aria-invalid:border-2 aria-invalid:border-danger disabled:bg-surface-muted";

interface ShellProps {
  label: ReactNode;
  /** Extra text under the field (format, rules). */
  hint?: ReactNode;
  /** Shown in red under the field and announced; marks the control invalid. */
  error?: string | undefined;
  /** Something in the label row on the right (e.g. "Forgot?", a character count). */
  labelAside?: ReactNode;
}

interface ControlIds {
  id: string;
  "aria-invalid": true | undefined;
  "aria-describedby": string | undefined;
}

/** Label + control + hint/error, wired for screen readers (aria-describedby, aria-invalid). */
function FieldShell({
  label,
  hint,
  error,
  labelAside,
  id,
  children,
}: ShellProps & { id: string | undefined; children: (ids: ControlIds) => ReactNode }) {
  const autoId = useId();
  const controlId = id ?? autoId;
  const hintId = `${controlId}-hint`;
  const errorId = `${controlId}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(" ") || undefined;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <label htmlFor={controlId} className={cn("text-sm font-bold", error ? "text-danger" : "text-ink-2")}>
          {label}
        </label>
        {labelAside}
      </div>
      {children({
        id: controlId,
        "aria-invalid": error ? true : undefined,
        "aria-describedby": describedBy,
      })}
      {hint && !error && (
        <p id={hintId} className="text-[13px] text-muted">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="text-[13px] font-medium text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

export function Field({
  label,
  hint,
  error,
  labelAside,
  className,
  id,
  ...props
}: ShellProps & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <FieldShell label={label} hint={hint} error={error} labelAside={labelAside} id={id}>
      {(ids) => <input {...ids} className={cn(inputClass, className)} {...props} />}
    </FieldShell>
  );
}

export function TextAreaField({
  label,
  hint,
  error,
  labelAside,
  className,
  id,
  ...props
}: ShellProps & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <FieldShell label={label} hint={hint} error={error} labelAside={labelAside} id={id}>
      {(ids) => (
        <textarea
          {...ids}
          rows={3}
          className={cn(inputClass, "h-auto min-h-24 resize-y py-3 leading-relaxed", className)}
          {...props}
        />
      )}
    </FieldShell>
  );
}

/** A native select: the phone's own picker on mobile, full keyboard support everywhere. */
export function SelectField({
  label,
  hint,
  error,
  labelAside,
  className,
  id,
  children,
  ...props
}: ShellProps & SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <FieldShell label={label} hint={hint} error={error} labelAside={labelAside} id={id}>
      {(ids) => (
        <select {...ids} className={cn(inputClass, "select-arrow pr-10", className)} {...props}>
          {children}
        </select>
      )}
    </FieldShell>
  );
}

/** "12 / 300" next to a label, turning red past the limit. */
export function CharacterCount({ value, max }: { value: string; max: number }) {
  return (
    <span
      aria-hidden="true"
      className={cn("tabular text-xs", value.length > max ? "font-bold text-danger" : "text-faint")}
    >
      {value.length} / {max}
    </span>
  );
}
